import React, { useState, useMemo } from "react";
import { Search, BookOpen, FileText, Video, Volume2, Image as ImageIcon, Code, ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";

interface Section {
  id: string;
  title: string;
  contents?: Content[];
  order?: number;
}

interface Content {
  id: string;
  title: string;
  type: string;
  order?: number;
}

interface LessonContentSelectorProps {
  sections: Section[];
  selectedSections: string[];
  selectedContents: string[];
  onSectionToggle: (sectionId: string) => void;
  onContentToggle: (contentId: string) => void;
  className?: string;
}

export default function LessonContentSelector({
  sections,
  selectedSections,
  selectedContents,
  onSectionToggle,
  onContentToggle,
  className = "",
}: LessonContentSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4" />;
      case "TEXT":
        return <FileText className="w-4 h-4" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4" />;
      case "CODE":
        return <Code className="w-4 h-4" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getContentTypeName = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const getContentTypeBadgeClass = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-yellow-100 text-yellow-700";
      case "TEST":
      case "EXAM":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const flattenedItems = useMemo(() => {
    const items: Array<{
      type: "section" | "content";
      data: Section | Content;
      sectionId?: string;
    }> = [];

    sections
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .forEach((section) => {
        // Her zaman bölümü ekle
        if (!searchTerm || section.title.toLowerCase().includes(searchTerm.toLowerCase())) {
          items.push({ type: "section", data: section });
        }

        // İçerikleri sadece bölüm expanded ise veya arama yapılıyorsa göster
        if (section.contents && (expandedSections[section.id] || searchTerm)) {
          section.contents
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .forEach((content) => {
              if (
                !searchTerm ||
                content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                section.title.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                items.push({
                  type: "content",
                  data: content,
                  sectionId: section.id,
                });
              }
            });
        }
      });

    return items;
  }, [sections, searchTerm, expandedSections]);

  const getSectionContentCount = (section: Section) => {
    return section.contents?.length || 0;
  };

  const getSectionSelectedContentCount = (section: Section) => {
    if (!section.contents) return 0;
    return section.contents.filter((content) => selectedContents.includes(content.id)).length;
  };

  return (
    <div className={`${className}`}>
      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
        <div className="p-2 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Bölüm veya içerik ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {flattenedItems.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">{searchTerm ? "Sonuç Bulunamadı" : "Bölüm Bulunamadı"}</div>
          ) : (
            <>
              {flattenedItems.map((item, index) => {
                if (item.type === "section") {
                  const section = item.data as Section;
                  const isSelected = selectedSections.includes(section.id);
                  const contentCount = getSectionContentCount(section);
                  const selectedContentCount = getSectionSelectedContentCount(section);
                  const isExpanded = expandedSections[section.id];

                  return (
                    <div key={`section-${section.id}`} className="border-b border-gray-100 flex items-center bg-white hover:bg-gray-50">
                      <div className="px-3 py-3 flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSectionToggle(section.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 focus:ring-1 focus:ring-offset-0"
                        />
                      </div>

                      <div className="flex-1 px-1 py-3 flex items-center gap-3 cursor-pointer" onClick={() => onSectionToggle(section.id)}>
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-gray-900">{section.title}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {contentCount > 0 && (
                              <>
                                {selectedContentCount > 0 && <span className="text-blue-600 font-medium">{selectedContentCount}/</span>}
                                {contentCount} içerik
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {contentCount > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleSection(section.id)}
                          className="px-3 py-3 text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  );
                } else {
                  const content = item.data as Content;
                  const isSelected = selectedContents.includes(content.id);

                  return (
                    <div
                      key={`content-${content.id}`}
                      className="flex items-center text-sm bg-white hover:bg-gray-50"
                      style={{ paddingLeft: "32px" }}>
                      <div className="px-3 py-2 flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onContentToggle(content.id)}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500 focus:ring-1 focus:ring-offset-0"
                        />
                      </div>

                      <div className="flex-1 px-1 py-2 flex items-center gap-3 cursor-pointer" onClick={() => onContentToggle(content.id)}>
                        <div
                          className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${getContentTypeBadgeClass(
                            content.type,
                          )}`}>
                          {getContentTypeIcon(content.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-gray-900">{content.title}</div>
                          <div className="text-xs text-gray-500 truncate">{getContentTypeName(content.type)}</div>
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
